// Retry-upload picker on the /presentations/{id} detail page when
// the row is in errored state. Picks a file → POSTs file_size_bytes
// to retry-upload → tus uploads to the returned URL → reloads so the
// server-rendered card flips to processing.

import {
  tusUpload,
  formatBytes,
  throttleByIntegerPercent,
} from "./presentation_tus.js";
import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-presentation-retry]";

export function initPresentationRetry(root = document) {
  const widgets = root.querySelectorAll
    ? root.querySelectorAll(ROOT_SELECTOR)
    : [];
  widgets.forEach(wireWidget);
}

function wireWidget(el) {
  if (!firstBind(el)) return;

  const retryURL = el.dataset.retryUrl;
  if (!retryURL) return;

  const input = el.querySelector("[data-presentation-retry-input]");
  if (!input) return;

  const status = el.querySelector("[data-presentation-retry-status]");
  const bar = el.querySelector("[data-presentation-retry-bar]");
  const message = el.querySelector("[data-presentation-retry-message]");
  const errorEl = el.querySelector("[data-presentation-retry-error]");

  input.addEventListener("change", async () => {
    const file = input.files && input.files[0];
    if (!file) return;

    hideError(errorEl);
    showStatus(status, message, "Provisioning upload…");
    input.disabled = true;

    let payload;
    try {
      const params = new URLSearchParams();
      params.append("file_size_bytes", String(file.size));
      const resp = await fetch(retryURL, {
        method: "POST",
        body: params,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!resp.ok) {
        const body = await readErrorMessage(resp);
        throw new Error(body);
      }
      payload = await resp.json();
    } catch (err) {
      console.error("presentations.retry: provision failed", err);
      resetForRetry(input, status);
      showError(errorEl, "Could not start the upload. Please try again.");
      return;
    }

    if (!payload || !payload.upload_url) {
      resetForRetry(input, status);
      showError(errorEl, "Server response was missing upload details.");
      return;
    }

    tusUpload({
      file,
      uploadURL: payload.upload_url,
      onError: (err) => {
        console.error("presentations.retry: tus upload failed", err);
        resetForRetry(input, status);
        showError(
          errorEl,
          "Upload failed. Refresh and try again with a fresh file.",
        );
      },
      onProgress: throttleByIntegerPercent((uploaded, total, pct) => {
        if (bar) bar.style.width = `${pct}%`;
        setMessage(
          message,
          `Uploading ${formatBytes(uploaded)} of ${formatBytes(total)} (${pct}%)`,
        );
      }),
      onSuccess: () => {
        if (bar) {
          bar.style.width = "100%";
          // Brief green confirmation makes the boundary intentional
          // before the page wipes via reload.
          bar.classList.remove("bg-brand-500");
          bar.classList.add("bg-emerald-500");
        }
        setMessage(message, "Upload complete. Your video is being processed…");
        setTimeout(() => window.location.reload(), 600);
      },
    });
  });
}

function resetForRetry(input, status) {
  input.disabled = false;
  // Clearing value lets the user re-pick the same file (otherwise
  // selecting an identical File object doesn't fire `change`).
  input.value = "";
  if (status) status.classList.add("hidden");
}

function showStatus(status, message, text) {
  if (status) status.classList.remove("hidden");
  setMessage(message, text);
}

function setMessage(node, text) {
  if (node) node.textContent = text;
}

function showError(el, text) {
  if (!el) return;
  el.textContent = text;
  el.classList.remove("hidden");
}

function hideError(el) {
  if (!el) return;
  el.classList.add("hidden");
  el.textContent = "";
}

async function readErrorMessage(resp) {
  try {
    const body = await resp.json();
    if (body && body.error) return body.error;
  } catch (_) {
    // Fall through.
  }

  return `Request failed (${resp.status}).`;
}
